import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { chatPanelQuery, chatPreviousQuery, type ChatNewMessage, type ChatPanel, type ChatRecommendedAction } from '../api/chatSupport'
import { deskRunQuery, giveBackToAdam, sendTicketChat, ticketChatQuery, type TicketChatMessage, type TicketChatPage } from '../api/easeedesk'
import { Spinner } from '../ui/Spinner'
import {
  ChatCannedMessages,
  ChatChangeTicketButton,
  ChatRecommendations,
  chatClosed,
  chatStamp,
  InviteUserModal,
  SuggestActionModal,
  useChatPolling,
} from './ChatSupport'
import './TicketChat.css'
import { NEW_TAB } from '../ui/NewTab'

/**
 * Fila "Chat" din fisa tichetului - `$.chat` din `chat_conversation.js`, cu tot
 * ce avea: istoricul, casuta de trimis, mesajele noi la 10 secunde
 * (`getNewMessages`), actiunile recomandate pe ultimul mesaj al clientului,
 * mesajele predefinite ("canned"), "Invite user to chat", "Change to <tip>" si
 * "Suggest new action". Uneltele si datele lor vin din `chat/panel` - acelasi
 * cap pe care il citeste panoul ecranului de suport.
 *
 * Compozitorul (13 octombrie 2026, a doua trecere) e al lui Adam: aceleasi
 * clase din `public/css/od_ai.css` - `od-ai-compose`, `od-ai-compose-field`,
 * `od-ai-send` (avionul de hartie in coltul casutei), `od-ai-act` pentru
 * uneltele mici de dedesubt si `od-ai-cmd-badge` pentru contor. Casuta
 * ingusta cu patru butoane mari sub ea nu semana cu nimic din portal.
 *
 * Asezarea (13 octombrie 2026): un antet cu omul cu care vorbesti si starea
 * discutiei, comutatorul "This ticket | Earlier chats", firul cu inaltime fixa
 * si bule, si compozitorul pe toata latimea, cu uneltele langa el. Inainte
 * erau pastila "This ticket" singura sus, caseta albastra "No messages!", o
 * casuta ingusta, o banda punctata goala (zona actiunilor recomandate, desenata
 * si cand nu era nicio actiune) si "Add canned messages" lipit jos.
 *
 * Ce NU exista in date si deci nu se deseneaza: starea online a clientului
 * (`chat/panel` nu o da) si atasamentele (chatul nu are fisiere, nici in
 * portal). Antetul arata starea DISCUTIEI - deschisa sau inchisa -, nu a omului.
 */

type Thread = TicketChatMessage | ChatNewMessage

function sourceOf(m: Thread): number {
  return 'source' in m ? m.source : 0
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter((p) => p !== '')
  if (parts.length === 0) return '?'
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}

/* ------------------------------------------------------------------------ */
/*  Firul                                                                    */
/* ------------------------------------------------------------------------ */

/**
 * Bulele. Operatorul (noi) in dreapta, clientul in stanga - ca in portal.
 * Culorile vin din setarile chatului firmei, cu aceeasi potrivire ciudata de
 * nume ca in `quick_chat_support.js`: `received_message_color` e a
 * OPERATORULUI, `sent_message_color` a clientului. Mesajele de pe portalul de
 * oaspete (`source = 1`) au culorile lor fixe.
 */
export function ChatThread({ messages, colors, empty }: { messages: Thread[]; colors?: ChatPanel['chat_settings']; empty: string }) {
  const box = useRef<HTMLDivElement>(null)
  const last = messages.length > 0 ? messages[messages.length - 1].id : 0

  /* Firul sta jos, pe ultimul mesaj - si cand vine unul nou. */
  useLayoutEffect(() => {
    const el = box.current
    if (el) el.scrollTop = el.scrollHeight
  }, [last])

  if (messages.length === 0) {
    return (
      <div className="tkc-thread tkc-thread-empty" ref={box}>
        <div className="tkc-empty">
          <i className="far fa-comments tkc-empty-icon" aria-hidden="true"></i>
          <p>{empty}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="tkc-thread" ref={box} role="log" aria-live="polite" aria-label="Chat messages">
      <ul className="tkc-list">
        {messages.map((message) => {
          const guest = sourceOf(message) === 1
          const ours = message.kind === 'agent'
          const color = ours ? (guest ? '#32777E' : colors?.received_message_color || '#3AACB3') : guest ? '#B4B4B4' : colors?.sent_message_color || '#575757'
          const author = !ours && guest ? 'GUEST' : message.author
          return (
            <li key={message.id} className={`tkc-line ${ours ? 'tkc-ours' : 'tkc-theirs'}${guest ? ' tkc-guest' : ''}`}>
              {!ours && (
                <span className="tkc-avatar" aria-hidden="true">
                  {initials(author)}
                </span>
              )}
              <div className="tkc-stack">
                <div className="tkc-meta">
                  <span className="tkc-author message-data-name">{author}</span>
                  <span className="tkc-time">{chatStamp(message.at)}</span>
                </div>
                <div className="tkc-bubble" style={{ background: color, '--tkc-bubble': color } as CSSProperties}>
                  {message.message}
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/*  Compozitorul                                                             */
/* ------------------------------------------------------------------------ */

const MIN_ROWS = 2
const MAX_ROWS = 6

/**
 * Casuta care creste cu textul (2-6 randuri), trimiterea ca iconita in coltul
 * ei (ca la Adam), Enter trimite,
 * Shift+Enter sau Ctrl+Enter pun rand nou (Ctrl+Enter era cel din portal;
 * Shift+Enter e cel pe care il incearca toata lumea). Cat pleaca mesajul,
 * butonul e oprit si un al doilea Enter nu mai trimite inca o data.
 */
function ChatComposer({
  id,
  text,
  onText,
  tools,
}: {
  id: number
  text: string
  onText(text: string): void
  tools: ReactNode
}) {
  const client = useQueryClient()
  const area = useRef<HTMLTextAreaElement>(null)
  /* `isPending` ajunge in randare abia la urmatorul tact; doua Enter-uri in
     acelasi tact ar trece amandoua de el. Zavorul sta in ref. */
  const inFlight = useRef(false)

  const send = useMutation({
    mutationFn: (message: string) => sendTicketChat(id, message),
    onSuccess: () => {
      onText('')
      void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id, 'chat'] })
    },
    onSettled: () => {
      inFlight.current = false
    },
  })

  /* Inaltimea dupa continut, intre 2 si 6 randuri. */
  useLayoutEffect(() => {
    const el = area.current
    if (!el) return
    const style = window.getComputedStyle(el)
    const line = parseFloat(style.lineHeight) || 20
    const pad = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0)
    const border = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0)
    el.style.height = 'auto'
    const wanted = Math.min(Math.max(el.scrollHeight, line * MIN_ROWS + pad), line * MAX_ROWS + pad) + border
    el.style.height = `${wanted}px`
    el.style.overflowY = el.scrollHeight > line * MAX_ROWS + pad ? 'auto' : 'hidden'
  }, [text])

  const submit = () => {
    if (text.trim() === '' || inFlight.current) return
    inFlight.current = true
    send.mutate(text)
  }

  const onKey = (ev: KeyboardEvent<HTMLTextAreaElement>) => {
    if (ev.key !== 'Enter' || ev.nativeEvent.isComposing) return
    if (ev.shiftKey) return
    ev.preventDefault()
    if (ev.ctrlKey || ev.metaKey) {
      const el = ev.currentTarget
      const at = el.selectionStart ?? text.length
      onText(text.slice(0, at) + '\n' + text.slice(el.selectionEnd ?? at))
      return
    }
    submit()
  }

  const busy = send.isPending

  return (
    <form
      className="tkc-composer send_chat_message"
      method="post"
      onSubmit={(ev) => {
        ev.preventDefault()
        submit()
      }}
    >
      <input type="hidden" name="ticket_id" value={id} />
      <div className="od-ai-compose tkc-compose">
        <div className="od-ai-compose-field">
          <textarea
            ref={area}
            className="tkc-input"
            name="message"
            rows={MIN_ROWS}
            placeholder="Type your message"
            aria-label="Type your message"
            title="Enter sends · Shift+Enter new line"
            value={text}
            readOnly={busy}
            onChange={(ev) => onText(ev.target.value)}
            onKeyDown={onKey}
          ></textarea>
          <button
            type="submit"
            className="od-ai-send send-message tkc-send"
            title={busy ? 'Sending...' : 'Send (Enter)'}
            aria-label={busy ? 'Sending...' : 'Send'}
            disabled={text.trim() === '' || busy}
            aria-busy={busy}
          >
            {busy ? (
              <>
                <i className="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
                <span className="sr-only">Sending...</span>
              </>
            ) : (
              <i className="fas fa-paper-plane" aria-hidden="true"></i>
            )}
          </button>
        </div>
        {send.isError && (
          <p className="text-danger tkc-error" role="alert">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i> The message was not sent: {(send.error as Error).message}
          </p>
        )}
      </div>
      <div className="tkc-toolbar">
        <div className="od-ai-acts tkc-tools">{tools}</div>
        <span className="tkc-hint">
          <kbd>Enter</kbd> to send, <kbd>Shift</kbd>+<kbd>Enter</kbd> new line
        </span>
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------------ */
/*  Discutiile de dinainte                                                   */
/* ------------------------------------------------------------------------ */

/**
 * Ce statea in pastilele de langa "This ticket": celelalte tichete deschise prin
 * chat de acelasi om (legaturi catre fisa lor, nu file inauntrul acesteia - asa
 * se citea conversatia altui tichet crezand ca e a celui din capul paginii),
 * plus ultima discutie de pe acelasi aparat (`chat/previous`, cea din
 * "Details" de pe ecranul de suport), numai de citit.
 */
function EarlierChats({ id, history, colors }: { id: number; history: TicketChatPage['history']; colors?: ChatPanel['chat_settings'] }) {
  const previous = useQuery(chatPreviousQuery(id))

  return (
    <div className="tkc-earlier">
      <h5 className="tkc-earlier-title">Other chat tickets from this contact</h5>
      {history.length === 0 ? (
        <p className="tkc-muted">No other chat tickets from this contact.</p>
      ) : (
        <ul className="tkc-earlier-list">
          {history.map((one) => (
            <li key={one.ticket}>
              <a className="tk-chat-old" href={`/app/easeedesk/ticket/${one.ticket}`} title={one.at} {...NEW_TAB}>
                <i className="far fa-comment-dots" aria-hidden="true"></i> Ticket {one.ticket}
              </a>
              <span className="tkc-muted"> - started {chatStamp(one.at)}</span>
            </li>
          ))}
        </ul>
      )}
      <h5 className="tkc-earlier-title">Previous conversation on this device</h5>
      {previous.error ? (
        <p className="text-danger">Something went wrong: {previous.error.message}</p>
      ) : !previous.data ? (
        <Spinner />
      ) : (
        <ChatThread messages={previous.data.messages} colors={colors} empty="No earlier conversation on this device." />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/*  Adam in discutie                                                         */
/* ------------------------------------------------------------------------ */

/**
 * Cand Adam vorbeste chiar acum cu clientul in aplicatie, antetul o spune -
 * altfel colegul scrie peste el. Cand Adam a predat tichetul colegului,
 * acesta (sau un super admin) il poate da inapoi dupa ce a lamurit ce era de
 * lamurit; Adam citeste conversatia si continua.
 */
function DeskInChat({ id }: { id: number }) {
  const client = useQueryClient()
  const desk = useQuery(deskRunQuery(id))
  const back = useMutation({
    mutationFn: () => giveBackToAdam(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: deskRunQuery(id).queryKey }),
  })
  const run = desk.data?.run
  if (!run) return null

  return (
    <>
      {run.live && (
        <span className="tkc-live" role="status">
          <i className="fas fa-robot" aria-hidden="true"></i> Adam is live with the customer
        </span>
      )}
      {run.can_give_back && (
        <button type="button" className="btn btn-default btn-xs tkc-give-back" disabled={back.isPending} onClick={() => back.mutate()} title="Adam reads this conversation and continues the ticket">
          <i className="fas fa-undo" aria-hidden="true"></i> {back.isPending ? 'Giving back...' : 'Give back to Adam'}
        </button>
      )}
      {back.isError && (
        <span className="text-danger tkc-error" role="alert">
          Not given back: {(back.error as Error).message}
        </span>
      )}
    </>
  )
}

/* ------------------------------------------------------------------------ */
/*  Fila                                                                     */
/* ------------------------------------------------------------------------ */

type View = 'ticket' | 'earlier'

export function TicketChat({ id }: { id: number }) {
  const { data, error } = useQuery(ticketChatQuery(id))
  const panel = useQuery(chatPanelQuery(id))
  const client = useQueryClient()
  const [text, setText] = useState('')
  const [recommended, setRecommended] = useState<ChatRecommendedAction[]>([])
  const [inviting, setInviting] = useState(false)
  const [suggesting, setSuggesting] = useState<string | null>(null)
  const [view, setView] = useState<View>('ticket')

  const active = !chatClosed(panel.data)
  /* Mesajele noi ale clientului se marcheaza citite pe server cand le aduce
     ceasul; contorul de pe fila ("Chat N") se citeste din fisa, deci fisa se
     reciteste - altfel ramanea pe numarul de la deschidere. */
  useChatPolling(id, active, setRecommended, () => void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id], exact: true }))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  const lastCustomer = [...data.messages].reverse().find((m) => m.kind === 'customer')
  const contact = panel.data?.affected_user || lastCustomer?.author || 'Customer'
  const device = panel.data?.affected_device
  const colors = panel.data?.chat_settings
  const pick = (m: string) => setText((have) => (have ? have + ' ' + m : m))

  const tools = (
    <>
      <ChatCannedMessages variant="act" messages={panel.data?.predefined_messages ?? []} onPick={pick} />
      <button type="button" className="od-ai-act tkc-act suggest-new-action" title="Suggest a new action for this request" data-ticket={id} onClick={() => setSuggesting(lastCustomer?.message ?? '')}>
        <i className="fas fa-lightbulb" aria-hidden="true"></i> <span className="tkc-act-label">Suggest</span>
      </button>
      <button type="button" className="od-ai-act tkc-act invite-users" title="Invite another user to this chat" data-ticket={id} onClick={() => setInviting(true)}>
        <i className="fa fa-user-plus" aria-hidden="true"></i> <span className="tkc-act-label">Invite</span>
      </button>
      {panel.data?.change_ticket && <ChatChangeTicketButton id={id} change={panel.data.change_ticket} variant="act" />}
    </>
  )

  return (
    <div className={`tk-chatc tkc chat-container-${id}`}>
      <header className="tkc-head">
        <div className="tkc-who">
          <span className="tkc-avatar tkc-avatar-lg" aria-hidden="true">
            {initials(contact)}
          </span>
          <div className="tkc-who-text">
            <div className="tkc-who-name">{contact}</div>
            <div className="tkc-who-sub">
              <span className={`tkc-state ${active ? 'tkc-state-open' : 'tkc-state-closed'}`}>
                <i className="fas fa-circle" aria-hidden="true"></i> {active ? 'Conversation open' : 'Conversation closed'}
              </span>
              {device && (
                <span className="tkc-device">
                  <i className="fas fa-desktop" aria-hidden="true"></i> {device.device_name}
                </span>
              )}
              <DeskInChat id={id} />
            </div>
          </div>
        </div>
        <div className="btn-group btn-group-sm tkc-switch chat-history" role="group" aria-label="Which conversation">
          <button type="button" className={`btn btn-default${view === 'ticket' ? ' active' : ''}`} aria-pressed={view === 'ticket'} onClick={() => setView('ticket')}>
            This ticket
          </button>
          <button type="button" className={`btn btn-default${view === 'earlier' ? ' active' : ''}`} aria-pressed={view === 'earlier'} onClick={() => setView('earlier')}>
            Earlier chats{data.history.length > 0 && <span className="badge tkc-count">{data.history.length}</span>}
          </button>
        </div>
      </header>

      {view === 'earlier' ? (
        <EarlierChats id={id} history={data.history} colors={colors} />
      ) : (
        <>
          <ChatThread messages={data.messages} colors={colors} empty="No messages yet - start the conversation below." />

          {active && recommended.length > 0 && (
            <div className="tkc-suggested">
              <span className="tkc-suggested-label">
                <i className="fas fa-magic" aria-hidden="true"></i> Suggested
              </span>
              <ChatRecommendations id={id} device={device?.ID ?? 0} actions={recommended} onPickMessage={pick} onSuggest={() => setSuggesting(lastCustomer?.message ?? '')} bare />
            </div>
          )}

          {active ? (
            <ChatComposer id={id} text={text} onText={setText} tools={tools} />
          ) : (
            <div className="tkc-closed">
              <i className="fas fa-lock" aria-hidden="true"></i> This conversation is closed. New messages cannot be sent from this ticket.
            </div>
          )}
        </>
      )}

      {inviting && <InviteUserModal id={id} onClose={() => setInviting(false)} />}
      {suggesting !== null && <SuggestActionModal id={id} keywords={suggesting} onClose={() => setSuggesting(null)} />}
    </div>
  )
}
